import { useAsync, UseAsyncStatus } from '@shared/async';
import { useErrorHandler } from '@shared/errors';
import { useHeaderDetails } from '@shared/header';
import { Box, ColumnConfig, DataTable, FormField, TextInput } from 'grommet';
import { Search } from 'grommet-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { HiOutlinePencil } from 'react-icons/hi2';
import { useNavigate } from 'react-router-dom';

import { TableEmptyPlaceholder, TableLoadingOverlay } from '@/components/loading';
import { Button } from '@/components-new/button';
import { useUserService } from '@/features/admin/api/use-users-service';
import { User } from '@/features/admin/types/user';

const UsersPage = () => {
  const { setHeaderDetails, clearHeaderDetails } = useHeaderDetails();
  const { getUsers } = useUserService();
  const { handleError } = useErrorHandler();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<{ search?: string }>({});

  const asyncUsers = useAsync(getUsers);
  const loadingUsers = asyncUsers.status === UseAsyncStatus.Pending;
  const filteredUsers = useMemo(
    () => asyncUsers.value?.filter(user =>
      !filters.search
      || user.name.toLowerCase().includes(filters.search.toLowerCase())
      || user.email.toLowerCase().includes(filters.search.toLowerCase())
    ) ?? [],
    [asyncUsers.value, filters]
  );

  useEffect(() => {
    const { status, error } = asyncUsers;
    if (status === UseAsyncStatus.Pending || status === UseAsyncStatus.Idle) return;

    if (status === UseAsyncStatus.Error) {
      handleError(error, { title: 'Failed to Load Users', message: 'We encountered an unexpected error while loading Users.' });
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncUsers.status]);

  const {
    control,
    handleSubmit,
    watch,
    reset
  } = useForm<{ search?: string }>({ mode: 'all', defaultValues: { search: '' } });

  React.useEffect(() => {
    void asyncUsers.execute();

    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setHeaderDetails({
      documentTitle: 'Users',
      pageTitle: 'Users',
      breadcrumbs: []
    });

    return () => {
      clearHeaderDetails();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEditUser = (id: number) => {
    navigate(`/admin/users/${id}/edit`);
  };

  const columns: ColumnConfig<User>[] = [
    { property: 'name', header: 'Name' },
    { property: 'email', header: 'Email' },
    {
      property: 'actions',
      size: 'xsmall',
      sortable: false,
      render: (user: User) => (
        <Box direction="row" gap="xxsmall">
          <Button
            plain
            title="Edit"
            onClick={() => onEditUser(user.id)}
          >
            <HiOutlinePencil/>
          </Button>
        </Box>
      )
    }
  ];

  useEffect(() => {
    // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'Timeout'.
    let timeoutId: NodeJS.Timeout = null;
    const subscription = watch(() => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        handleSubmit((data) => setFilters(data))();
      }, 300);
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box pad={{ bottom: 'medium' }}>
      <Box margin={{ top: 'small' }} gap="small">
        <Box
          as="form"
          border={{ size: 'small', color: 'light-3', side: 'bottom' }}
          pad="small"
          direction="row"
          justify="end"
          align="end"
          gap="medium"
          onReset={(e) => {
            e.preventDefault();
            reset();
          }}
        >
          <Box direction="row" gap="small" align="end">
            <Box width="medium">
              <Controller
                control={control}
                name="search"
                render={({ field: { ref, value, onChange, onBlur, name } }) => (
                  <FormField margin="none" name={name} htmlFor={name} aria-label="Search By Name or Email">
                    <TextInput
                      ref={ref}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      id={name}
                      name={name}
                      type="search"
                      icon={<Search color="accent-1"/>}
                      placeholder="Search By Name or Email"
                    />
                  </FormField>
                )}
              />
            </Box>
          </Box>

          <Button type="reset" aria-label="Clear">Clear</Button>
        </Box>

        <DataTable
          columns={columns}
          data={filteredUsers}
          primaryKey="id"
          sortable
          step={10}
          paginate
          placeholder={
            (loadingUsers || filteredUsers.length === 0)
            && <Box fill>
              {loadingUsers && <TableLoadingOverlay/>}
              {!loadingUsers && filteredUsers.length === 0 && <TableEmptyPlaceholder content="No User data is available."/>}
            </Box>
          }
        />
      </Box>
    </Box>
  );
};

export default UsersPage;
